import { Card, Flex, Tooltip } from '@brightdrop/bd-ui';
import { type MRT_ColumnDef, Table } from '@brightdrop/table-ui';
import { Box, IconButton } from '@mui/material';
import { useTranslation } from 'react-i18next';
import InfoIcon from '~/assets/icons/new/infoIcon.svg?react';
import TranslatedLabel, {
  translateText,
} from '~/common/components/TranslatedLabel/TranslatedLabel';
import type { ManageSectionProps } from '~/features/integrations/partners/types';

import useStyles, { getTableContainerProps } from './DataSharedCard.styles';
import useOverviewCardStyles from './overview-card.styles';

const TRANSLATION_KEYS = {
  title: 'integrations:account.manage.dataSharedTitle',
  tooltip: 'integrations:account.manage.dataSharedTooltip',
  description: 'integrations:account.manage.dataSharedDescription',
  dataType: 'integrations:account.manage.dataType',
  shared: 'integrations:account.manage.shared',
} as const;

type SharedDataRow = {
  key: string;
  dataType: string;
  shared: string;
};

/**
 * Data this integration sends to the partner account.
 * Row labels come from the partner config. The shared column stays blank until values are wired.
 */
const DataSharedCard = ({ partner }: ManageSectionProps): JSX.Element => {
  const { classes } = useStyles();
  const { classes: cardClasses } = useOverviewCardStyles();
  useTranslation('integrations');

  const { manage, nameKey } = partner;
  const partnerName = translateText(nameKey);
  const rows: SharedDataRow[] = manage.dataSharedRows.map((row) => ({
    key: row.key,
    dataType: translateText(row.labelKey),
    shared: '',
  }));

  const columns: MRT_ColumnDef<SharedDataRow>[] = [
    {
      accessorKey: 'dataType',
      header: translateText(TRANSLATION_KEYS.dataType),
    },
    {
      accessorKey: 'shared',
      header: translateText(TRANSLATION_KEYS.shared),
      size: 87,
      minSize: 87,
      grow: false,
      enableResizing: false,
      muiTableHeadCellProps: { align: 'center' },
      muiTableBodyCellProps: { align: 'center' },
    },
  ];

  return (
    <Card dataTestId="account-integrations-data-shared">
      <Flex col gap={4} className={classes.header}>
        <Flex className={classes.titleRow}>
          <TranslatedLabel
            translationKey={TRANSLATION_KEYS.title}
            variables={{ partner: partnerName }}
            typographyVariant="header2_medium"
            className={cardClasses.title}
            component="h2"
          />
          <Tooltip
            content={translateText(TRANSLATION_KEYS.tooltip)}
            placement="top"
            showArrow
            trigger={
              <IconButton
                className={cardClasses.tooltipIcon}
                size="small"
                aria-label={translateText(TRANSLATION_KEYS.tooltip)}
                data-testid="account-integrations-data-shared-tooltip"
              >
                <InfoIcon />
              </IconButton>
            }
          />
        </Flex>
        <TranslatedLabel
          translationKey={TRANSLATION_KEYS.description}
          variables={{ partner: partnerName }}
          typographyVariant="small_body_regular"
          className={cardClasses.secondary}
        />
      </Flex>
      <Box className={classes.tableWrapper}>
        <Table
          renderNoRecordFound={<span />}
          tablePropsObj={{
            columns,
            data: rows,
            enableTopToolbar: false,
            enableBottomToolbar: false,
            enablePagination: false,
            muiTableContainerProps: getTableContainerProps(),
          }}
        />
      </Box>
    </Card>
  );
};

export default Object.assign(DataSharedCard, {
  sectionId: 'data-shared',
});
